// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Dispatch } from 'redux';
import { API_URL } from 'Config';
import { getJson, patchJson, postJson, putJson, deleteRequest } from 'Utils/http';
import { GetAdminProjectsRoute, GetAdminGeneralRoute, SIGN_UP_SUCCESS } from 'Utils/routes';
import history from 'Utils/history';
import moment from 'moment-timezone';
import { GetOrgStatsStartAction, GetOrgStatsSuccessAction, GetOrgUsersSuccessAction } from 'Features/account/accountActions';
import type { RootState } from 'Store';
import { SetSortFilterAction } from 'Features/projectList/projectListActions';
import { EditOrderSuccessAction } from 'Features/order/orderActions';
import { CreateOrgFailureAction, CreateOrgStartAction, CreateOrgSuccessAction, DeleteOrgAction, DeleteProjectAdminFailureAction,
  DeleteProjectAdminStartAction, DeleteProjectAdminSuccessAction, GetLastLoginFailureAction, GetLastLoginStartAction, GetLastLoginSuccessAction,
  GetLoginStatsFailureAction, GetLoginStatsStartAction, GetLoginStatsSuccessAction, GetOrgProjectsFailureAction, GetOrgProjectsStartAction,
  GetOrgProjectsSuccessAction, GetPricingAction, GetPricingOverrideConfigSuccessAction, GetSubscriptionFailureAction, GetSubscriptionStartAction, GetSubscriptionSuccessAction,
  GetPromotionsFailureAction, GetPromotionsStartAction, GetPromotionsSuccessAction,
  GetUsersStartAction, GetUsersSuccessAction, LoadListStartAction, LoadListSuccessAction, OrgEmailsFailureAction, OrgEmailsSuccessAction,
  SetLoadAllAction, SetLoadNewAction, SetOrgPricingFailureAction, SetOrgPricingStartAction, SetOrgPricingSuccessAction, SetRowsPerPageAction,
  SetSearchFilterAction, SetUserIdAction, UpdateCostPerAcreAction, UpdateOrgFailureAction, UpdateOrgSuccessAction, GetOrgAction, SetListAction,
  SetOrgIdAction, SetPageAction, GetOrgCreditCardsAction,
  GetOrderAdjustmentsSuccessAction, GetCommittedTiersAction, SetCommittedTierStartAction,
  SetCommittedTierSuccessAction, SetCommittedTierFailureAction } from './adminActions';

moment.tz.setDefault('America/New_York');

export const GetOrgThunk =
  (orgId: string) => async (dispatch: Dispatch, getState: () => RootState) => {
    const url = `${API_URL}/admins/getOrgDetails/${orgId}`;
    const { token } = getState().auth;

    const result = await getJson<IOrgResponse>(url, token);
    if (result.success) {
      dispatch(GetOrgAction(result.data.org));
    }

    return result;
  };

export const SetOrgIdThunk = (data: string) => (dispatch: Dispatch) =>
  dispatch(SetOrgIdAction(data));

export const SetSearchFilterThunk = (searchFilter: string) => (dispatch: Dispatch) => dispatch(SetSearchFilterAction(searchFilter));
export const SetSortFilterThunk = (sortFilter: string) => (dispatch: Dispatch) => dispatch(SetSortFilterAction(sortFilter));
export const SetPageThunk = (page: number) => (dispatch: Dispatch) => dispatch(SetPageAction(page));
export const SetRowsPerPageThunk = (rowsPerPage: number) => (dispatch: Dispatch) => dispatch(SetRowsPerPageAction(rowsPerPage));

export const SetListThunk = (newData: Array<IAdminUser>) => (dispatch: Dispatch) => dispatch(SetListAction(newData));

export const SetLoadAll = (newData: boolean) => (dispatch: Dispatch) => dispatch(SetLoadAllAction(newData));

export const SetLoadNew = (newData: boolean) => (dispatch: Dispatch) => dispatch(SetLoadNewAction(newData));

export const GetListThunk = () => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const { page, rowsPerPage, searchFilter, sortFilter, loadNew } = getState().admin;
  dispatch(LoadListStartAction());

  const url = `${API_URL}/admins/dashboard?limit=${rowsPerPage}&page=${page}&query=${searchFilter || ''}&sort=${sortFilter}`;
  const { token } = getState().auth;

  const result = await getJson<IOrgResponse>(url, token);
  if (!result.success) {
    // Loading must clear on failure - the dashboard search field disables while it is set.
    dispatch(SetLoadAllAction(getState().admin.loadAll));
    return;
  }

  const data = {
    list: result.data.orgs,
    count: result.data.stats.totalOrgCount,
  };

  if (loadNew) {
    dispatch(SetListAction([]));
  }

  if (data.list.length === 0) {
    dispatch(SetLoadAllAction(true));
  } else {
    dispatch(LoadListSuccessAction(data));
  }

  dispatch(SetLoadNewAction(false));
};

export const GetOrgProjectsThunk = (orgId: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const { token } = getState().auth;
  const url = `${API_URL}/admins/getOrgProjects/${orgId}`;
  dispatch(GetOrgProjectsStartAction());
  const result = await getJson<IAdminProjectsResponse[]>(url, token);
  if (!result.success) {
    dispatch(GetOrgProjectsFailureAction());
  } else {
    dispatch(GetOrgProjectsSuccessAction(result.data.projects));
  }
};

export const DeleteProjectAdminThunk = (projectId: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const { token } = getState().auth;
  const { orgId } = getState().admin;
  const url = `${API_URL}/projects/${projectId}`;

  dispatch(DeleteProjectAdminStartAction());
  const result = await deleteRequest(url, token);
  if (result.success) {
    history.push(GetAdminProjectsRoute(orgId));
    dispatch(DeleteProjectAdminSuccessAction(projectId));
  } else {
    dispatch(DeleteProjectAdminFailureAction());
  }
};

// The "Check your email" page echoes the address the verification link went to
const redirectToSignUpSuccess = (email: string) => {
  history.push(SIGN_UP_SUCCESS, { email });
};

/**
 * Function to call the API to create a new organization
 * and dispatch the CreateOrgAsync action
 */
export const CreateOrgThunk = (data: { firstName: string, lastName: string, phoneNumber?: string, companyName: string, email: string, jobTitle?: string, captchaToken?: string, industry?: string }) => async (dispatch: Dispatch, getState: () => RootState) => {
  dispatch(CreateOrgStartAction());
  const { firstName, lastName, email, companyName, phoneNumber, jobTitle, captchaToken, industry } = data;
  const url = `${API_URL}/users/createOrgAdmin`;
  const result = await postJson<IOrgResponse>(url, {
    firstName,
    lastName,
    email,
    companyName,
    captchaToken,
    ...(phoneNumber && { phoneNumber }),
    ...(jobTitle && { jobTitle }),
    ...(captchaToken && { captchaToken }),
    ...(industry && { industry }),
    contact_created_by: _ADMIN_ ? 'Created by AW Admin' : 'Created by Self Signup',
  });

  if (!result.success) {
    dispatch(CreateOrgFailureAction(result.message));
    return result;
  }

  const { organization } = result.data;
  if (organization) {
    dispatch(CreateOrgSuccessAction(organization));
    if (_ADMIN_) {
      history.push(GetAdminGeneralRoute(organization._id));
    } else {
      redirectToSignUpSuccess(email);
    }
    return result;
  }

  // A 2xx without an organization is the enumeration-safe "If this email is eligible" answer for an existing
  // org or user. Nothing was created: clear the loading flag (the reducer discards the message).
  dispatch(CreateOrgFailureAction(result.data.message));
  if (!_ADMIN_) {
    // Self-signup must not reveal whether the email exists, so it lands on the success page as designed
    redirectToSignUpSuccess(email);
    return result;
  }
  // The admin portal shows the server's message as the form error instead of navigating to a missing org
  const failedResult: IApiResponse<IOrgResponse> = { success: false, message: result.data.message };
  return failedResult;
};

export const GetOrgSubscriptionThunk = (orgId: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/subscriptions/getOrgActiveSubscription/${orgId}`;
  const { token } = getState().auth;

  dispatch(GetSubscriptionStartAction());
  const result = await getJson<ISubscriptions>(url, token);

  if (result.success) {
    dispatch(GetSubscriptionSuccessAction(result.data));
  } else {
    dispatch(GetSubscriptionFailureAction(result.message));
  }
  return result;
};

export const GetOrgCreditCardsThunk = (stripeId: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/subscriptions/getCards?stripeId=${stripeId}`;
  const { token } = getState().auth;

  const result = await getJson<ICreditCard[]>(url, token);
  if (result.success) {
    dispatch(GetOrgCreditCardsAction(result.data));
  }
};

export const UpdateOrgThunk = (data: any) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/organizations/updateOrgDetails/${data._id}`;
  const { token } = getState().auth;

  const result = await patchJson<IOrgResponse>(
    url,
    data,
    token,
  );
  if (result.success) {
    dispatch(UpdateOrgSuccessAction(result.data.org));
  } else {
    dispatch(UpdateOrgFailureAction(result.message));
  }

  return result;
};

/**
 * Set the account's one license-fee setting (AUT-6569): added to each order, prepaid, or none. Pricing access and
 * a note are required server-side; the caller refreshes the org to pick up the setting and its history.
 */
export const UpdateLicenseFeeThunk = (mode: string, prepaidCadence: string | null, note: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/organizations/updateLicenseFee`;
  const { token } = getState().auth;
  const { orgId } = getState().admin;
  const result = await patchJson<{ message: string, licenseFee: ILicenseFee, entry: ILicenseFeeHistoryEntry }>(
    url,
    {
      ownerOrganization: orgId,
      mode,
      prepaidCadence,
      note,
    },
    token,
  );
  return result;
};

/**
 * Function to call the API to get users in an organization with the orgId
 * and dispatch the GetUsersAsync action
 */
export const GetOrgUsersThunk = () => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const { token } = getState().auth;
  const { orgId } = getState().admin;
  const url = `${API_URL}/admins/getOrgUsers/${orgId}`;

  dispatch(GetUsersStartAction());

  const result = await getJson<IUser[]>(url, token);

  if (!result.success) {
    return result;
  }

  if (result.success) {
    dispatch(GetUsersSuccessAction(result.data.data));
  }
  return result;
};

/**
 * Function to call the API to get user object for a particular userId
 * and dispatch the GetOrgUserAsync action
 */
export const GetOrgUserThunk = (userId: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/users/${userId}`;
  const { token } = getState().auth;

  const result = await getJson<IUser>(url, token);
  if (result.success) {
    dispatch(GetOrgUsersSuccessAction(result.data));
  }

  return result;
};

export const SetUserIdThunk = (userId: string) => (dispatch: Dispatch) =>
  dispatch(SetUserIdAction(userId));

export const GetLastLoginThunk = (orgId: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const url = `${API_URL}/organizations/getLastLogin/${orgId}`;
    const { token } = getState().auth;

    dispatch(GetLastLoginStartAction());
    const result = await getJson<{ lastLogin: string }>(url, token);
    if (result.success) {
      const timeString = result.data.lastLogin === 'N/A' ? result.data.lastLogin : moment(result.data.lastLogin).format('MM/DD/YYYY | hh:mm A');
      dispatch(GetLastLoginSuccessAction({ lastLogin: timeString }));
    } else {
      dispatch(GetLastLoginFailureAction(result.message));
    }

    return result;
  };

export const GetLoginStatsThunk = (orgId: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/organizations/getLoginStats/${orgId}`;
  const { token } = getState().auth;

  dispatch(GetLoginStatsStartAction());
  const result = await getJson<{ thisMonth: thisMonthStats, perMonth: perMonthStats }>(url, token);
  if (result.success) {
    dispatch(GetLoginStatsSuccessAction(result.data));
  } else {
    dispatch(GetLoginStatsFailureAction(result.message));
  }

  return result;
};

export const GetOrgStatsThunk = (id: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/organizations/getOrgStats/${id}`;
  const { token } = getState().auth;

  dispatch(GetOrgStatsStartAction());

  const result = await getJson<IOrgStats>(url, token);
  if (result.success) {
    dispatch(GetOrgStatsSuccessAction(result.data));
  }

  return result;
};

export const UpdateCostPerAcreThunk = (costPerAcre: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/organizations/updateCostPerAcre`;
  const { token } = getState().auth;
  const { orgId } = getState().admin;
  const result = await patchJson<IOrgResponse>(
    url,
    {
      orgId,
      costPerAcre,
    },
    token,
  );
  if (result.success) {
    dispatch(UpdateCostPerAcreAction(result.data.org.costPerAcre));
  }
  return result;
};

export const UpdatePaygRateThunk = (rate: number | null, notes: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/organizations/updatePaygRate`;
  const { token } = getState().auth;
  const { orgId } = getState().admin;
  const result = await patchJson<{ message: string, entry: IPaygRateHistoryEntry }>(
    url,
    {
      ownerOrganization: orgId,
      rate,
      notes,
    },
    token,
  );
  return result;
};

/**
 * Set or revert the account's overage override (AUT-6571): the base rate every credit beyond its balance is priced
 * at, in place of the ladder spot its usage reaches. A null rate reverts to the ladder.
 */
export const UpdateOverageRateThunk = (rate: number | null, notes: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/organizations/updateOverageRate`;
  const { token } = getState().auth;
  const { orgId } = getState().admin;
  const result = await patchJson<{ message: string, entry: IPaygRateHistoryEntry, overageRate: IOverageRate | null }>(
    url,
    {
      ownerOrganization: orgId,
      rate,
      notes,
    },
    token,
  );
  return result;
};

export const AdjustOrgCreditsThunk = (deltaCredits: number, notes: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/organizations/adjustCredits`;
  const { token } = getState().auth;
  const { orgId } = getState().admin;
  const result = await patchJson<{ message: string, entry: ICreditAdjustmentEntry, balance: ICreditAdjustmentBalance }>(
    url,
    {
      ownerOrganization: orgId,
      deltaCredits,
      notes,
    },
    token,
  );
  return result;
};

export const RemoveEstimatesThunk = (orgId: string, estimateIds: string[]) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/orders/removeEstimates`;
  const { token } = getState().auth;

  const result = await postJson(url, {
    orgId,
    estimateIds,
  }, token);

  return result;
};

export const DeleteOrgThunk = (orgId: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/organizations/removeOrgPII`;
  const { token } = getState().auth;

  const data = { orgId };

  const result = await deleteRequest(url, token, data);

  if (result.success) {
    dispatch(DeleteOrgAction(orgId));
  }

  return result;
};

export const OrgEmailsThunk = () => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/organizations/optOutOfEmails`;
  const { token } = getState().auth;

  const { orgId } = getState().admin;

  const data = { orgId };

  if (!orgId) {
    dispatch(OrgEmailsFailureAction('ordId undefined'));
    return;
  }

  const result = await patchJson<IOrgEmailsResponse>(url, data, token);

  if (result.success) {
    dispatch(OrgEmailsSuccessAction(result.data.optOutOfEmails));
  }
};

export const GetPricingThunk = () => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/admins/pricing`;
  const { token } = getState().auth;

  const result = await getJson<{message: string, pricing: IPricingTier[]}>(url, token);
  if (result.success) {
    dispatch(GetPricingAction(result.data.pricing));
  }
};

// Every promotion with its usage, for the read-only admin Promotions page (AUT-6455)
export const GetPromotionsThunk = () => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/admins/promotions`;
  const { token } = getState().auth;

  dispatch(GetPromotionsStartAction());
  const result = await getJson<IAdminPromotionsResponse>(url, token);
  if (result.success) {
    dispatch(GetPromotionsSuccessAction(result.data.promotions));
  } else {
    dispatch(GetPromotionsFailureAction());
  }
};

export const AssignPricingPlanThunk = (pricingData: { startDate: string | Date, endDate?: string | Date, pricingPlanName: string, startingCredits?: number, creditsPurchased?: number, contractUrl?: string }) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/organizations/assignPricingPlan`;
  const { token } = getState().auth;
  const ownerOrganization = getState().admin.orgId;
  const data = { ...pricingData, ownerOrganization };

  dispatch(SetOrgPricingStartAction());
  const result = await postJson<{message: string, newOrganizationPlan: IPricingPlanInfo}>(url, data, token);
  if (result.success) {
    dispatch(SetOrgPricingSuccessAction(result.data.newOrganizationPlan));
    // we do this to pull express ai subscription if exists and set automate to null
    GetOrgSubscriptionThunk(ownerOrganization)(dispatch, getState);
    GetOrgThunk(ownerOrganization)(dispatch, getState);
  } else {
    dispatch(SetOrgPricingFailureAction());
  }
  return result.success;
};
/*
  * Function to fetch the active committed pricing tiers for the admin assign-tier dropdown
*/
export const GetCommittedTiersThunk = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const { token } = getState().auth;
  const url = `${API_URL}/catalog/committedTiers`;
  const result = await getJson<{ message: string, committedTiers: ICommittedTier[] }>(url, token);

  if (result.success) {
    dispatch(GetCommittedTiersAction(result.data.committedTiers));
  }

  return result.success;
};

/*
  * Function to assign a committed pricing tier to the currently-selected org
*/
export const AssignCommittedTierThunk = (tierData: {
  tierId: string,
  annualCommit?: number,
  dollarPerCredit: number,
  discountPercent: number,
  startDate: string | Date,
  endDate?: string | Date,
  note: string,
}) => async (dispatch: Dispatch, getState: () => RootState) => {
  const url = `${API_URL}/organizations/assignCommittedTier`;
  const { token } = getState().auth;
  const ownerOrganization = getState().admin.orgId;
  const data = { ...tierData, ownerOrganization };

  dispatch(SetCommittedTierStartAction());
  const result = await postJson<{ message: string, newCommittedTier: ICommittedTierInfo }>(url, data, token);
  if (result.success) {
    dispatch(SetCommittedTierSuccessAction(result.data.newCommittedTier));
    GetOrgThunk(ownerOrganization)(dispatch, getState);
  } else {
    dispatch(SetCommittedTierFailureAction());
  }
  return result.success;
};

export const GetOrgPastPlansThunk = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const { token } = getState().auth;
  const { orgId } = getState().admin;
  const url = `${API_URL}/admins/getOrgPastPlans/${orgId}`;
  const result = await getJson<{message: string, orgPastPlans: IOrgPastPlan[]}>(url, token);

  if (result.success) {
    return result.data?.orgPastPlans;
  }

  return [];
};

/*
  * Function to get bundle deliverables for a project
*/
export const GetDeliverablesThunk = (orderId:string, projectId:string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const { token } = getState().auth;
  const { orgId } = getState().admin;
  const url = `${API_URL}/deliverables/getBundleDeliverables`;
  const data = { orderId, projectId, orgId };

  const result = await postJson<{message: string, addOns: AddOns[]}>(url, data, token);

  if (result.success) {
    return result.data?.addOns;
  }

  return [];
};

/*
  * Function to get order adjustments for an organization
*/
export const GetOrderAdjustmentsThunk = (searchQuery: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const { orgId } = getState().admin;

  const url = searchQuery && searchQuery.trim()
    ? `${API_URL}/admins/getOrderAdjustments/${orgId}?query=${searchQuery}`
    : `${API_URL}/admins/getOrderAdjustments/${orgId}`;
  const { token } = getState().auth;
  const result = await getJson<IOrderAdjustmentsDetailsResponse>(url, token);

  if (result.success) {
    dispatch(GetOrderAdjustmentsSuccessAction(result.data.data));
  }
};

// --- Order pricing overrides (AUT-6328) ---

// The override type config is static per deploy — fetch once per session and cache in the slice.
export const GetPricingOverrideConfigThunk = () => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const cached = getState().admin.pricingOverrideConfig;
  if (cached) return { success: true as const, data: cached };

  const url = `${API_URL}/pricingOverrideConfig`;
  const { token } = getState().auth;
  const result = await getJson<IPricingOverrideConfig>(url, token);
  if (result.success) {
    dispatch(GetPricingOverrideConfigSuccessAction(result.data));
  }

  return result;
};

// Merge the response's order + adjustments into one payload for the order slice's list merge.
// pricingOverride is spread as an explicit own key on purpose: the slice merges with
// Object.assign, which never removes keys, and a cleared override comes back with the
// subdocument absent — without the explicit key the stale override would stay visible.
const toOrderRefreshPayload = (updatedOrder: IPricingOverrideResponse['updatedOrder']): IOrder => ({
  ...updatedOrder.order,
  pricingOverride: updatedOrder.order.pricingOverride,
  creditAdjustments: updatedOrder.creditAdjustments,
});

export const SetPricingOverrideThunk = (data: { orderId: string; overrideType: string; values: IPricingOverrideValues; notes?: string }) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const { orderId, overrideType, values, notes } = data;
  const url = `${API_URL}/orders/${orderId}/pricingOverride`;
  const { token } = getState().auth;

  const result = await putJson<IPricingOverrideResponse>(url, { overrideType, values, notes }, token);
  if (result.success) {
    dispatch(EditOrderSuccessAction(toOrderRefreshPayload(result.data.updatedOrder)));
  }

  return result;
};

export const ClearPricingOverrideThunk = (orderId: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/orders/${orderId}/pricingOverride`;
  const { token } = getState().auth;

  const result = await deleteRequest<IPricingOverrideResponse>(url, token);
  if (result.success) {
    dispatch(EditOrderSuccessAction(toOrderRefreshPayload(result.data.updatedOrder)));
  }

  return result;
};

// History is rendered locally by the override dialog — no slice storage.
export const GetPricingOverrideHistoryThunk = (orderId: string) => async (
  _dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/orders/${orderId}/pricingOverrideHistory`;
  const { token } = getState().auth;
  return getJson<IPricingOverrideHistoryResponse>(url, token);
};
